import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { deLong } from "@/components/PlannerWeekView";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
const de = (iso) => (iso ? iso.split("-").reverse().join(".") : "—");
const TYPE = { urlaub: "Urlaub", krank: "Krank", fortbildung: "Fortbildung", dienstreise: "Dienstreise", sonstiges: "Abwesenheit" };

function RescheduleDialog({ vac, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  useEffect(() => {
    api.get(`/planner/vacation/${vac.vacation_id}/reschedule`).then((r) => {
      setData(r.data);
      setSel(Object.fromEntries(r.data.items.filter((i) => i.new && !i.confirmed_client).map((i) => [i.appointment_id, true])));
    }).catch(err);
  }, [vac]);
  const apply = async () => {
    const items = data.items.filter((i) => sel[i.appointment_id] && i.new).map((i) => ({ appointment_id: i.appointment_id, date: i.new.date, time_from: i.new.time_from }));
    if (!items.length) { toast.error("Keine Termine ausgewählt"); return; }
    try {
      const { data: r } = await api.post(`/planner/vacation/${vac.vacation_id}/reschedule`, { items });
      toast.success(`${r.rescheduled} Termin(e) verschoben, Teilnehmer informiert${r.conflicts.length ? ` · ${r.conflicts.length} Konflikt(e)` : ""}`);
      onDone();
    } catch (e) { err(e); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vacation-reschedule-dialog">
        <DialogHeader><DialogTitle>Termine wegen {TYPE[vac.type] || "Abwesenheit"} {de(vac.start_date)}–{de(vac.end_date)} verschieben</DialogTitle></DialogHeader>
        {!data ? <div className="text-xs text-slate-400">Berechne…</div> : data.items.length === 0 ? <div className="text-sm text-slate-500">Keine Termine im Zeitraum.</div> : (
          <>
            <p className="text-xs text-slate-600">Vorschau alt → neu. Vom Teilnehmer bereits bestätigte Termine sind nicht vorausgewählt – bitte bewusst anhaken. Gespeichert und informiert wird erst nach „Übernehmen“.</p>
            {data.items.map((i) => (
              <label key={i.appointment_id} className={`flex items-start gap-3 border px-3 py-2 text-sm ${i.new ? "border-slate-200" : "border-rose-200 bg-rose-50"}`} data-testid={`resched-${i.appointment_id}`}>
                <input type="checkbox" disabled={!i.new} checked={!!sel[i.appointment_id]} onChange={(e) => setSel({ ...sel, [i.appointment_id]: e.target.checked })} className="mt-1" data-testid={`resched-check-${i.appointment_id}`} />
                <div>
                  <b>{i.client_name}</b>{i.confirmed_client && <span className="text-[10px] uppercase tracking-overline text-amber-700"> · vom Teilnehmer bestätigt</span>}
                  <div className="text-xs text-slate-600"><s>{deLong(i.old.date)} {i.old.time_from}</s> → {i.new ? <b>{deLong(i.new.date)} {i.new.time_from}–{i.new.time_to}</b> : <span className="text-rose-700">{i.warning}</span>}</div>
                </div>
              </label>
            ))}
            <div className="flex gap-2">
              <button onClick={apply} className="flex-1 py-3 bg-slate-900 text-white text-sm hover:bg-black" data-testid="resched-apply">Übernehmen & Teilnehmer informieren</button>
              <Link to="/vertretungsplan" className="px-4 py-3 border border-slate-300 text-sm hover:bg-slate-100">Stattdessen Vertretung</Link>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PlannerReviewPanel({ refreshKey }) {
  const [d, setD] = useState(null);
  const [vac, setVac] = useState(null);
  const load = () => api.get("/planner/review").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line
  if (!d) return null;
  const pending = d.late.filter((a) => a.cancellation?.credit_status === "pending_review");
  return (
    <div className="space-y-6" data-testid="planner-review">
      {d.sync_errors > 0 && <div className="border border-rose-300 bg-rose-50 px-4 py-3 text-xs text-rose-800" data-testid="review-sync-errors">{d.sync_errors} Termin(e) nicht mit Google Kalender synchronisiert</div>}
      <div className="border border-slate-200 bg-white" data-testid="review-vacations">
        <div className="px-4 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Urlaub & Abwesenheiten</div>
        {d.vacations.length === 0 && <div className="px-4 py-3 text-xs text-slate-500">Keine anstehenden Abwesenheiten.</div>}
        {d.vacations.slice(0, 8).map((v) => (
          <div key={v.vacation_id} className="px-4 py-2 border-b border-slate-50 text-xs flex justify-between items-center gap-2">
            <span>{v.coach_name} · {TYPE[v.type] || "Abwesenheit"} {de(v.start_date)}–{de(v.end_date)}{v.affected ? <b className="text-rose-700"> · {v.affected} Termin(e) betroffen</b> : ""}</span>
            {v.affected > 0 && <button onClick={() => setVac(v)} className="px-2 py-1 border border-slate-900 hover:bg-slate-100" data-testid={`review-resched-${v.vacation_id}`}>Verschieben</button>}
          </div>
        ))}
      </div>
      <div className="border border-slate-200 bg-white" data-testid="review-handover">
        <div className="px-4 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Bereit zur Übergabe ({d.handover_ready.length})</div>
        {d.handover_ready.map((h) => <div key={h.client_id + h.part_no} className="px-4 py-2 text-xs border-b border-slate-50"><Link to={`/avgs/${h.client_id}`} className="font-medium hover:underline">{h.name}</Link> · Coach {h.part_no} kann planen · Ende {de(h.end_date)}</div>)}
      </div>
      <div className="border border-slate-200 bg-white" data-testid="review-late">
        <div className="px-4 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Kurzfristige Absagen & Nichterscheinen – Prüfung offen ({pending.length})</div>
        {pending.map((a) => <div key={a.appointment_id} className="px-4 py-2 text-xs border-b border-slate-50"><Link to={`/avgs/${a.client_id}`} className="font-medium hover:underline">{a.client_name}</Link> · {de(a.date)} {a.time_from} · {a.status === "no_show" ? "Nicht erschienen" : `${a.cancellation?.hours_before} Std. vorher abgesagt`}</div>)}
      </div>
      {vac && <RescheduleDialog vac={vac} onClose={() => setVac(null)} onDone={() => { setVac(null); load(); }} />}
    </div>
  );
}
